import { ChainId } from '@src/configs/chainInfo'
import { WRAPPED_GAS_TOKENS } from '@src/configs/chainInfo'
import { LZ_CHAIN_IDS } from '@src/configs/chainInfo'
import { MAX_TOKEN_DISPLAY_DECIMALS } from '@src/constants/exchange'
import { ExchangeType } from '@src/enums'
import { IExchangeData } from '@src/types/exchange'
import { INetwork } from '@src/types/network'
import { IQuoteData } from '@src/types/order'
import { Address, IToken } from '@src/types/token'
import { getChecksumAddress, isTronAddress } from '@src/utils/address'
import { addressToBytes32 } from '@src/utils/addressBytes32'
import { Balance, computeUSDAmount } from '@src/utils/balance'
import { exchangeLogger } from '@src/utils/logger'
import { solanaExchangeUtils } from '@src/utils/solana/exchangeUtils'
import { mapTokenAddress } from '@src/utils/tokenAddressMapping'
import { TronConverter } from '@src/utils/tron/tronAddressConverter'

// Checks if there are any errors in the exchange
export const computeHasErrors = (
    isInsufficientBalance: boolean,
    isQuoteInvalid: boolean,
    isBuyOutputLoading: boolean,
    isQuoteExpired: boolean,
    isDestinationSolana: boolean,
    sellTokenSymbol?: string
) => {
    return (
        isInsufficientBalance ||
        isQuoteInvalid ||
        isBuyOutputLoading ||
        isQuoteExpired ||
        (isDestinationSolana && sellTokenSymbol !== 'USDC')
    )
}

// Computes the miles based on the sell balance, sell value, tokens, and referral code
export const computeMiles = (
    buyData: IExchangeData,
    sellData: IExchangeData,
    tokens: IToken[],
    boundReferralCode: string
): { miles: number; multiplier: number } => {
    // Return 0 miles for wrap/unwrap operations
    if (isWrapOnly(sellData, buyData) || isUnWrapOnly(sellData, buyData)) {
        return { miles: 0, multiplier: 1 }
    }

    // Fallback implementation using local calculations
    // This is kept as a fallback if the API call fails
    const fallbackCalculation = () => {
        // Get USD price of the associated token from allUSDPrices
        const token = tokens.find(o => o.address === sellData.token.address && o.chainId === sellData.token.chainId)

        if (token && token.price_current && token.price_current > 0) {
            const miles = computeUSDAmount(token, sellData.value) ?? 0
            // if source/dest chain is Solana/Tron/Unichain, apply 2x multiplier regardless of referral code
            const isDoubledMilesChainsInvolved = [ChainId.Solana, ChainId.Tron, ChainId.Unichain].some(chain =>
                [sellData.chainId, buyData.chainId].includes(chain)
            )

            const baseMultiplier = boundReferralCode === 'MACH' ? 1.05 : boundReferralCode ? 1.15 : 1
            const multiplier = isDoubledMilesChainsInvolved ? baseMultiplier * 2 : baseMultiplier

            return { miles: miles * multiplier, multiplier }
        }

        return { miles: 0, multiplier: 1 }
    }

    // Return a promise-compatible result that mimics the shape of the async function result
    // but synchronously returns the fallback calculation for backward compatibility
    return fallbackCalculation()
}

// Checks if the sell and buy tokens are potentially CCTP compatible
export const isPotentiallyCCTP = (
    sellToken: string | undefined,
    buyToken: string | undefined,
    sellNetworkId: number | string,
    buyNetworkId: number | string
): boolean => {
    return sellToken === 'USDC' && buyToken === 'USDC' && sellNetworkId !== buyNetworkId
}

// Formats the exchange amount with commas
export const formatExchangeAmountWithCommas = (
    data: IExchangeData,
    maxDecimals: number = MAX_TOKEN_DISPLAY_DECIMALS
): string => {
    return new Balance(data.token).setValue(data.value).formatWithCommas(maxDecimals)
}

/**
 * Returns the appropriate swap function parameters based on exchange type and other data.
 * @param exchangeType
 * @param walletAddress
 * @param sellData
 * @param buyData
 * @param quote
 * @param gasEstimate
 * @param availableNetworks
 * @returns
 */
export const getSwapFunctionParameters = async (
    exchangeType: string,
    walletAddress: string | undefined,
    sellData: IExchangeData,
    buyData: IExchangeData,
    quote: IQuoteData,
    gasEstimate: boolean = false,
    availableNetworks: Record<string, INetwork>,
    flattened: boolean = false
) => {
    exchangeLogger.debug('[getTransactionFunctionArgs] [setMax]: received params:', {
        exchangeType: exchangeType,
        walletAddress: walletAddress,
        sellData: sellData,
        buyData: buyData,
        quote: quote,
    })

    let swapFunctionArgs

    if (exchangeType === ExchangeType.order_book) {
        swapFunctionArgs = encodePlaceOrderParameters(sellData, buyData, quote, gasEstimate, flattened)
    } else if (exchangeType === ExchangeType.cctp) {
        if (buyData.chainId === ChainId.Solana) {
            swapFunctionArgs = solanaExchangeUtils.getSwapFunctionParameters!(
                walletAddress,
                sellData,
                buyData,
                availableNetworks,
                flattened
            )
        } else {
            const amount = sellData.value || BigInt(0)
            const destinationAddressInBytes32 = addressToBytes32(walletAddress || '0x', buyData.chainId)

            if (flattened) {
                // Flattened arguments
                swapFunctionArgs = [
                    { type: 'uint256', value: amount.toString() },
                    { type: 'uint32', value: availableNetworks[buyData.chainId].cctp_id.toString() },
                    { type: 'bytes32', value: destinationAddressInBytes32 },
                    { type: 'address', value: mapTokenAddress(sellData.token.address, false, sellData.chainId) },
                ]
            } else {
                swapFunctionArgs = [
                    amount,
                    availableNetworks[buyData.chainId].cctp_id,
                    destinationAddressInBytes32,
                    mapTokenAddress(sellData.token.address, false, sellData.chainId),
                ]
            }
        }
    }

    exchangeLogger.debug('[getSwapFunctionParameters]: swapFunctionArgs=', {
        swapFunctionArgs,
    })
    return swapFunctionArgs
}

/**
 * Encodes contract call parameters for `placeOrder((address,address,uint32),(uint96,uint96,uint16,address,uint96),(uint32,uint16,uint16),address,address)`
 * @param sellData
 * @param buyData
 * @param quoteData
 * @param gasEstimate
 * @returns
 */
export const encodePlaceOrderParameters = (
    sellData: IExchangeData,
    buyData: IExchangeData,
    quoteData: IQuoteData,
    gasEstimate: boolean = false,
    flattened: boolean = false
): any[] => {
    let returnValue: any[] = []

    // Skip validation if doing gas estimation
    if (!gasEstimate) {
        if (quoteData.reject_order) {
            throw new Error('Swap amount is too low to cover transaction fees.')
        }

        if (
            getChecksumAddress(mapTokenAddress(sellData.token.address, true, sellData.chainId)) !==
                getChecksumAddress(mapTokenAddress(quoteData.src_asset_address, true, sellData.chainId)) ||
            getChecksumAddress(mapTokenAddress(buyData.token.address, true, buyData.chainId)) !==
                getChecksumAddress(mapTokenAddress(quoteData.dst_asset_address, true, buyData.chainId))
        ) {
            throw new Error('Invalid quote: token address mismatch')
        }

        if (LZ_CHAIN_IDS[buyData.token.chainId] < 0) {
            throw new Error(`No LayerZero chain id found for token: ${JSON.stringify(buyData.token)}`)
        }
    }

    const { order_direction, order_funding, order_expiration, filler_address, target_address } = quoteData.order_data

    exchangeLogger.debug('[encodePlaceOrderParameters] order_direction:', order_direction)
    exchangeLogger.debug('[encodePlaceOrderParameters] order_funding:', order_funding)
    exchangeLogger.debug('[encodePlaceOrderParameters] order_expiration:', order_expiration)
    exchangeLogger.debug('[encodePlaceOrderParameters] filler_address:', filler_address)
    exchangeLogger.debug('[encodePlaceOrderParameters] target_address:', target_address)

    // Handle Tron addresses
    const _srcAssetAddress = order_direction.src_token_address
    let _dstAssetAddress = order_direction.dst_token_address

    if (buyData.token.chainId === ChainId.Tron) {
        if (buyData.token && isTronAddress(order_direction.dst_token_address)) {
            _dstAssetAddress = TronConverter.toChecksumEVM(order_direction.dst_token_address) as Address
        } else {
            exchangeLogger.warn(
                'Tron address expected here, because buyData.token.chainId=ChainId.Tron. But received:',
                {
                    address: order_direction.dst_token_address,
                }
            )
        }
    }

    exchangeLogger.debug('[encodePlaceOrderParameters] buyData.chainId:', buyData.chainId)

    // First map the token address, then convert to bytes32 if feature is enabled
    exchangeLogger.debug('[encodePlaceOrderParameters] _dstAssetAddress:', _dstAssetAddress)
    const dstAsset = addressToBytes32(mapTokenAddress(_dstAssetAddress, true, buyData.chainId), buyData.chainId)

    // Convert target_address to bytes32 if feature is enabled
    exchangeLogger.debug('[encodePlaceOrderParameters] target_address:', target_address)
    const targetAddressValue = addressToBytes32(target_address, buyData.chainId)

    exchangeLogger.debug('[encodePlaceOrderParameters] targetAddressValue:', targetAddressValue)
    exchangeLogger.debug('[encodePlaceOrderParameters] dstAsset:', dstAsset)

    if (flattened) {
        returnValue = [
            {
                type: 'address',
                value: mapTokenAddress(_srcAssetAddress, true, sellData.chainId),
            },
            {
                type: 'bytes32',
                value: dstAsset,
            },
            { type: 'uint32', value: order_direction.dst_lzc.toString() },
            { type: 'uint96', value: BigInt(order_funding.src_amount_in).toString() },
            {
                type: 'uint96',
                value: BigInt(order_funding.dst_amount_out).toString(),
            },
            { type: 'uint16', value: order_funding.bond_fee.toString() },
            { type: 'address', value: order_funding.bond_token_address },
            { type: 'uint96', value: BigInt(order_funding.bond_amount).toString() },
            { type: 'uint32', value: order_expiration.timestamp.toString() },
            { type: 'uint16', value: order_expiration.challenge_offset.toString() },
            { type: 'uint16', value: order_expiration.challenge_window.toString() },
            {
                type: 'bytes32',
                value: targetAddressValue,
            },
            { type: 'address', value: filler_address },
        ]
    } else {
        returnValue = [
            [mapTokenAddress(_srcAssetAddress, true, sellData.chainId), dstAsset, order_direction.dst_lzc],
            [
                BigInt(order_funding.src_amount_in),
                BigInt(order_funding.dst_amount_out),
                order_funding.bond_fee,
                order_funding.bond_token_address,
                BigInt(order_funding.bond_amount),
            ],
            [order_expiration.timestamp, order_expiration.challenge_offset, order_expiration.challenge_window],
            targetAddressValue,
            filler_address,
        ]
    }

    exchangeLogger.debug('[encodePlaceOrderParameters]: returnValue=', {
        returnValue,
    })
    return returnValue
}

// Checks if the sell and buy tokens are wrapped only
export const isWrapOnly = (sellData: IExchangeData, buyData: IExchangeData) => {
    return (
        sellData.chainId === buyData.chainId &&
        buyData.token.address.toLowerCase() === WRAPPED_GAS_TOKENS[buyData.chainId].address.toLowerCase() &&
        sellData.token.native === true
    )
}

// Checks if the sell and buy tokens are unwrapped only
export const isUnWrapOnly = (sellData: IExchangeData, buyData: IExchangeData) => {
    return (
        sellData.chainId === buyData.chainId &&
        sellData.token.address.toLowerCase() === WRAPPED_GAS_TOKENS[sellData.chainId].address.toLowerCase() &&
        buyData.token.native === true
    )
}
